import { createApi } from '@reduxjs/toolkit/query/react'
import { backendBaseQuery } from '@shared/api'
import { Chat, ChatDTO, ChatMessagesListDTO, ChatsListDTO } from '../model'
import { APP_CONFIG } from '@shared/config'
import { io, Manager } from "socket.io-client";
import { useUserPlatformId } from '@shared/hooks';
import { ChatMessageDTO } from '../model/ChatDTO';

// Define a service using a base URL and expected endpoints
export const chatApi = createApi({
  reducerPath: 'chat-api',
  baseQuery: backendBaseQuery,
  tagTypes: ['Chat'],
  endpoints: (build) => ({
    getChats: build.query<ChatsListDTO, void>({
      query: () => ({
        url: `/chats`, 
        method: 'GET',
      }),
      providesTags: [
        {type: 'Chat', id: 'LIST'}
      ],
      async onCacheEntryAdded(
        arg,
        { dispatch, updateCachedData, cacheDataLoaded, cacheEntryRemoved },
      ) {
        const namespace = '/chats-ws'
        const wsURL = APP_CONFIG.backendURL+namespace
        const userPlatformId = localStorage.getItem('userPlatformId')
        const socket = io(
          wsURL, 
          {
            transports: ["websocket"],
            path: '/api'+namespace,
            query: {
              userPlatformId
            }
            // TODO Authorization
          }
        )
        try {
          await cacheDataLoaded

          socket.on('new-chat-message', (data) => {
            console.log('new chat message', data)
            updateCachedData(draft => {
              draft.chats = draft.chats.map(chat => {
                if(chat._id !== data.chatId) return chat
                chat.lastMessage = data
                return chat
              })
            })
            dispatch(
              chatApi.util.invalidateTags(
                [
                  {type: 'Chat', id: data.chatId+'-MESSAGES'}
                ]
              )
            )
          })
          socket.on('update-chat-message-status', (data) => {
            console.log('update chat message status', data)
            const chatsToUpdate: string[] = []
            updateCachedData(draft => {
              draft.chats = draft.chats.map(chat => {
                if(chat.lastMessage._id !== data._id) return chat
                chat.lastMessage.status = data.status
                chatsToUpdate.push(chat._id)
                return chat
              })
            })
            dispatch(
              chatApi.util.invalidateTags(
                chatsToUpdate.map(chatId => ({type: 'Chat', id: chatId+'-MESSAGES'}))
              )
            )
          })
          socket.on('ping', () => {
            console.log('ws ping')
          })
          socket.on('error', (error) => {
            console.error('ws error', error)
          })
        } catch {}
        await cacheEntryRemoved
        socket.close()
        console.log('socket closed')
      },
    }),
    getChatById: build.query<ChatDTO, Chat['_id']>({
      query: (id: Chat['_id']) => ({
        url: `/chats/${id}`,
        method: 'GET'
      }),
      providesTags: (result, error, id) => [
        { type: 'Chat', id }
      ],
    }),
    getChatMessages: build.query<ChatMessagesListDTO, Chat['_id']>({
      query: (id: Chat['_id']) => ({
        url: `/chats/${id}/messages`,
        method: 'GET'
      }),
      providesTags: (result, error, id) => [
        { type: 'Chat', id: id + '-MESSAGES'}
      ],
      async onCacheEntryAdded(
        arg,
        { dispatch, updateCachedData, cacheDataLoaded, cacheEntryRemoved },
      ) {
        const namespace = '/chats-ws'
        const wsURL = APP_CONFIG.backendURL+namespace
        const userPlatformId = localStorage.getItem('userPlatformId')
        const socket = io(
          wsURL, 
          {
            transports: ["websocket"],
            path: '/api'+namespace,
            query: {
              userPlatformId
            }
            // TODO Authorization
          }
        )
        try {
          await cacheDataLoaded

          socket.on('new-chat-message', (data) => {
            console.log('new chat message', data)
            updateCachedData(draft => {
              draft.messages.push(data)
            })
          })
          socket.on('update-chat-message-status', (data) => {
            console.log('update chat message status', data)
            
            updateCachedData(draft => {
              draft.messages = draft.messages.map(message => {
                if(message._id !== data._id) return message
                message.status = data.status
                return message
              })
            })
          })
          socket.on('ping', () => {
            console.log('ws ping')
          })
          socket.on('error', (error) => {
            console.error('ws error', error)
          })
        } catch {}
        await cacheEntryRemoved
        socket.close()
        console.log('socket closed 2')
      },
    }),
    markMessageAsReaded: build.mutation({
      query: (chatId: ChatMessageDTO['_id']) => ({
        url: `/chats/${chatId}/readed`,
        method: 'PUT'
      }),
      invalidatesTags: (result, error, chatId) => [
        {type: 'Chat', id: chatId +'-MESSAGES'}, 
        {type: 'Chat', id: 'LIST'}
      ]
    }),
    sendMessage: build.mutation({
      query: (body: {messageType: string, text: string, senderName: string, chatId: string}) => ({
        url: `/chats/${body.chatId}/message`,
        method: 'POST',
        body
      }),
      invalidatesTags: (result, error, body) => [
        {type: 'Chat', id: body.chatId +'-MESSAGES'}, 
        {type: 'Chat', id: 'LIST'},
      ]
    }),
  }),
})

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const { 
  useGetChatsQuery, 
  useGetChatByIdQuery,
  useGetChatMessagesQuery,
  useMarkMessageAsReadedMutation,
  useSendMessageMutation
} = chatApi